import { Box, CircularProgress, Typography } from '@mui/material';
import React, { RefObject } from 'react';
import { Palette } from 'Palette';
import { StyledButton, StyledButtonGroup } from '../../../components/styles/StyledButtons';
import StyledSection from '../../../components/styles/StyledSection';
import { StyledTypography } from '../../../components/styles/styles';
import ChatAlertsSection from './ChatAlertsSection';
import NotificationEventRow from './NotificationEventRow';
import { EventType } from './settingsTypes';
import { useChatNotificationPreferences } from './useChatNotificationPreferences';

interface NotificationSubscriptionsTabProps {
  loading: boolean;
  saving: boolean;
  events: EventType[];
  hasChanges: boolean;
  chatAlertsSectionRef: RefObject<HTMLDivElement | null>;
  onToggle: (eventTypeId: string) => void;
  onSave: () => void;
  chat: ReturnType<typeof useChatNotificationPreferences>;
}

const NotificationSubscriptionsTab: React.FC<NotificationSubscriptionsTabProps> = ({
  loading,
  saving,
  events,
  hasChanges,
  chatAlertsSectionRef,
  onToggle,
  onSave,
  chat,
}) => {
  const getEventType = (id: string) => events.find((e) => e.id === id);
  const allEoiSubscribed = getEventType('ALL_EOI_SUBMISSION')?.subscribed || false;
  const referralAcceptedSubscribed =
    getEventType('REFERRAL_INVITATION_ACCEPTED')?.subscribed || false;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which events you want to receive email notifications for.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <StyledSection tone="tone1" child>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 2,
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${Palette.tone1.border}`,
                backgroundColor: Palette.tone1.background,
              }}
            >
              <StyledTypography sx={{ fontWeight: 600 }}>Event</StyledTypography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    ml: '-11px',
                  }}
                >
                  <StyledTypography sx={{ fontWeight: 600 }}>Subscribed</StyledTypography>
                </Box>
              </Box>
            </Box>

            {events.map((event) => (
              <NotificationEventRow
                key={event.id}
                event={event}
                allEoiSubscribed={allEoiSubscribed}
                referralAcceptedSubscribed={referralAcceptedSubscribed}
                saving={saving}
                onToggle={onToggle}
              />
            ))}
          </Box>

          <StyledButtonGroup justifyContent="center" sx={{ mt: 3 }}>
            <StyledButton
              onClick={onSave}
              disabled={saving || !hasChanges}
              sx={{
                minWidth: '8.5rem',
                backgroundColor: Palette.default.buttonBg,
                color: Palette.default.buttonText,
                '&:hover': {
                  backgroundColor: Palette.tone1.buttonBg,
                  color: Palette.tone1.buttonText,
                },
              }}
            >
              {saving ? <CircularProgress size={20} /> : 'Save changes'}
            </StyledButton>
          </StyledButtonGroup>
        </StyledSection>
      )}

      <Box ref={chatAlertsSectionRef} sx={{ mt: 4, mb: 2, scrollMarginTop: '1rem' }}>
        <Typography variant="body2" color="text.secondary">
          Add channels to receive alerts when investors send chat messages. Each channel has its own
          timing. If an investor sends several messages before you read, you&apos;ll only get one
          alert for that thread. No external alert is sent while that chat is open in the app with
          this browser tab active and focused.
        </Typography>
      </Box>

      <ChatAlertsSection chat={chat} />
    </Box>
  );
};

export default NotificationSubscriptionsTab;
